import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Panel, Loading, ErrorNote, Empty, formatDateTime } from '../components/ui';
import { navigate } from '../lib/router';
import { useAuth } from '../lib/auth';

interface DashboardData {
  counts: Record<string, string | number>;
  attention: { kind: string; n: string; label: string }[];
  recent: { action: string; subject_type: string | null; created_at: string; user_email: string | null }[];
  monthlySpendPence: number;
}

const ATTENTION_TARGET: Record<string, () => void> = {
  'extraction-review': () => navigate('breakdown'),
  'comms-blocked': () => navigate('explain'),
  'translations-stale': () => navigate('translate'),
  'profiles-unapproved': () => navigate('explain'),
};

export function Dashboard() {
  const { user } = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardData>('/api/dashboard'),
  });

  if (isLoading) return <Loading what="Loading your dashboard" />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const n = (key: string) => Number(data.counts[key] ?? 0);

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={`${user?.organisationName ?? 'Your organisation'} — contracts, communications and what needs attention.`}
      />

      <div className="metrics-grid">
        <Metric label="Contracts" value={n('documents')} subtext={`${n('practices')} practices`} />
        <Metric
          label="Extracted items"
          value={n('items')}
          subtext={`${n('items_accepted')} accepted, ${n('items_pending')} awaiting review`}
        />
        <Metric
          label="Approved communications"
          value={n('comms_approved')}
          subtext={n('comms_blocked') ? `${n('comms_blocked')} blocked` : 'None blocked'}
        />
        <Metric
          label="Approved translations"
          value={n('translations_approved')}
          subtext={n('translations_stale') ? `${n('translations_stale')} need re-checking` : 'All current'}
        />
      </div>

      <div className="two-column">
        <Panel title="Needs attention" icon="!">
          {data.attention.length === 0 ? (
            <Empty>Nothing is waiting on you.</Empty>
          ) : (
            <div className="stack-16">
              {data.attention.map((a) => (
                <div className="checklist-item" key={a.kind}>
                  <div>
                    <div className="cell-title">{a.n} {a.label.toLowerCase()}</div>
                  </div>
                  <button
                    className="btn btn-secondary btn-small push-right"
                    onClick={ATTENTION_TARGET[a.kind]}
                  >
                    Open
                  </button>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Recent activity" icon="≡">
          {data.recent.length === 0 ? (
            <Empty>No activity recorded yet.</Empty>
          ) : (
            <div className="timeline">
              {data.recent.map((r, i) => (
                <div className="tl-item" key={i}>
                  <div className="tl-when">{formatDateTime(r.created_at)}</div>
                  <div className="tl-what">{humanise(r.action)}</div>
                  <div className="tl-who">{r.user_email ?? 'System'}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="This month" icon="£">
        <div className="kv">
          <span className="fact-label">Model processing cost</span>
          <span className="fact-value">£{(data.monthlySpendPence / 100).toFixed(2)}</span>
        </div>
        <p className="field-hint">
          What it cost InBrief to process your documents this month. Shown so the figure is never a
          surprise; it does not affect your subscription.
        </p>
      </Panel>
    </>
  );
}

function Metric({ label, value, subtext }: { label: string; value: number; subtext: string }) {
  return (
    <div className="metric-card">
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value.toLocaleString('en-GB')}</div>
      <div className="metric-subtext">{subtext}</div>
    </div>
  );
}

const humanise = (action: string) =>
  action
    .replace(/\./g, ' ')
    .replace(/^(\w)/, (c) => c.toUpperCase())
    .replace(/\b(uploaded|viewed|approved|generated|accepted|edited|rejected|deleted|started|answered)\b/, (w) => w);
